import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2, HardDrive, Clock, Infinity as InfinityIcon, Settings, GripVertical, Gift } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

function fmtSize(bytes) {
  if (bytes >= 1099511627776) return `${(bytes / 1099511627776).toFixed(bytes % 1099511627776 === 0 ? 0 : 1)} TB`;
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(bytes % 1073741824 === 0 ? 0 : 1)} GB`;
  if (bytes >= 1048576) return `${Math.round(bytes / 1048576)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

export function PricingDialog({ open, onOpenChange, tiers = [], currentTier }) {
  const [busy, setBusy] = useState(null);
  const isPaid = currentTier && currentTier !== "starter";

  const subscribe = async (tierId) => {
    setBusy(tierId);
    try {
      const { data } = await api.post("/billing/subscribe", {
        tier: tierId, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-5xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Choose your plan</DialogTitle>
          <DialogDescription className="text-white/50">
            More space, no 30-day expiry. Cancel anytime.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
          {tiers.map((t) => {
            const isCurrent = t.id === currentTier;
            const isFree = t.price_cents === 0;
            const accent = t.id === "pro";
            return (
              <div
                key={t.id}
                data-testid={`tier-card-${t.id}`}
                className={`relative rounded-2xl border p-5 flex flex-col ${
                  accent ? "border-[#00F0FF]/50 bg-[#00F0FF]/5" : "border-white/10 bg-white/[0.02]"
                }`}
              >
                {accent && (
                  <span className="absolute -top-2.5 left-5 rounded-full bg-[#00F0FF] text-black text-[10px] font-bold px-2.5 py-0.5 uppercase tracking-wider">
                    Popular
                  </span>
                )}
                <h3 className="font-display text-lg font-bold">{t.name}</h3>
                <div className="mt-2 flex items-end gap-1">
                  <span className="font-display text-3xl font-extrabold">
                    {isFree ? "Free" : `$${(t.price_cents / 100).toFixed(0)}`}
                  </span>
                  {!isFree && <span className="text-white/40 text-sm mb-1">/mo</span>}
                </div>

                <ul className="mt-4 space-y-2 text-sm text-white/70 flex-1">
                  <li className="flex items-center gap-2">
                    <HardDrive className="h-4 w-4 text-[#00F0FF]" /> {fmtSize(t.bytes)} storage
                  </li>
                  <li className="flex items-center gap-2">
                    {t.expiry_days ? (
                      <><Clock className="h-4 w-4 text-[#FFDD03]" /> Vaults expire in {t.expiry_days} days</>
                    ) : (
                      <><InfinityIcon className="h-4 w-4 text-emerald-400" /> No expiry</>
                    )}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-emerald-400" /> Unlimited vaults & clips
                  </li>
                  <li className="flex items-center gap-2">
                    <Gift className="h-4 w-4 text-[#FFDD03]" />
                    {t.comp_max == null
                      ? "Unlimited complimentary downloads"
                      : `${t.comp_max} complimentary downloads`}
                  </li>
                  {!isFree && (
                    <li className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-[#00F0FF]" /> Drag-to-reorder clips
                    </li>
                  )}
                </ul>

                <button
                  disabled={isCurrent || isFree || busy === t.id}
                  onClick={() => subscribe(t.id)}
                  data-testid={`subscribe-${t.id}`}
                  className={`mt-5 w-full rounded-full py-2.5 text-sm font-semibold transition-transform duration-300 disabled:opacity-50 ${
                    accent ? "bg-[#00F0FF] text-black hover:scale-[1.02]"
                           : "bg-white/10 text-white hover:bg-white/15"
                  }`}
                >
                  {busy === t.id ? (
                    <span className="inline-flex items-center gap-2 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</span>
                  ) : isCurrent ? "Current plan" : isFree ? "Default plan" : `Choose ${t.name}`}
                </button>
              </div>
            );
          })}
        </div>

        {isPaid && (
          <div className="mt-4 flex items-center justify-center">
            <button
              onClick={openPortal}
              disabled={busy === "portal"}
              data-testid="manage-subscription-btn"
              className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
            >
              {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings className="h-4 w-4" />}
              Manage or cancel subscription
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export { fmtSize };

